import React, { Fragment, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Row, Col, Alert, Spinner } from 'reactstrap';
import { formatDate } from '../../../utils/helpers';
import classnames from 'classnames';
import { ThemedLinkLink } from '../../../components/themed/ThemedComponents';
import {
    Block,
    FlexDiv,
    Heading4,
    SpaceBetweenDiv,
    TextDefault,
    ThemedButton,
    TooltipWrapper,
    ProgressWithNumber,
} from '@awarego-ui/components';
import RequireRole from '../../../containers/RequireRole';
import { trackingDisabled } from '../../../utils/constants';
import TableClient from '../../../components/table/TableClient';

function UserCampaignDetails({
    userDetails,
    anonymousTracking = false,
    onSendReminder,
    email,
    showTrainings = false,
    canRemind,
    loadingCanRemind,
}) {
    const subjects = userDetails.subjects ? userDetails.subjects : [];
    const score = Math.round(userDetails.score) || 0;

    // Define columns for TableClient
    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'Lesson',
                accessor: 'title',
                sortType: 'alphanumeric',
            },
            {
                Header: 'Delivery date',
                accessor: 'delivered',
                Cell: ({ value }) => value ? formatDate(value) : '- no data -',
                sortType: 'basic',
                sortMethod: (rowA, rowB, columnId) => {
                    const a = rowA.values[columnId];
                    const b = rowB.values[columnId];
                    
                    // Handle null/undefined values
                    if (!a && !b) return 0;
                    if (!a) return 1;
                    if (!b) return -1;
                    
                    // Convert to Date objects for comparison
                    const dateA = new Date(a);
                    const dateB = new Date(b);
                    
                    // Check if dates are valid
                    if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
                    if (isNaN(dateA.getTime())) return 1;
                    if (isNaN(dateB.getTime())) return -1;
                    
                    return dateA.getTime() - dateB.getTime();
                },
            },
            {
                Header: 'Viewed',
                accessor: 'viewed',
                Cell: ({ value }) => (
                    value > 0 ? (
                        <img
                            className="check-mark"
                            alt="icon"
                            title="Viewed"
                            src="/static/img/icon-checked.svg"
                        />
                    ) : (
                        <img
                            className="check-mark not-checked"
                            alt="icon"
                            title="Not viewed"
                            src="/static/img/icon-not-checked.svg"
                        />
                    )
                ),
                sortType: 'basic',
            },
            {
                Header: 'Correctly answered',
                accessor: 'correct',
                Cell: ({ value }) => (
                    value > 0 ? (
                        <img
                            className="check-mark"
                            alt="icon"
                            title="Answered correctly"
                            src="/static/img/icon-checked.svg"
                        />
                    ) : (
                        <img
                            className="check-mark not-checked"
                            alt="icon"
                            title="Not answered"
                            src="/static/img/icon-not-checked.svg"
                        />
                    )
                ),
                sortType: 'basic',
            },
            {
                Header: 'Incorrect count',
                accessor: 'incorrect',
                sortType: 'basic',
            },
        ];

        // Add trainings column if showTrainings is true
        if (showTrainings) {
            baseColumns.splice(1, 0, {
                Header: 'Trainings',
                accessor: 'trainings',
                Cell: ({ value, row }) => (
                    <span className="d-flex flex-column">
                        {row.original.training_ids.map((x) => (
                            <ThemedLinkLink
                                key={x}
                                to={`/trainings/${x}/view`}
                            >
                                {' '}
                                {userDetails.trainings[x].name}{' '}
                            </ThemedLinkLink>
                        ))}
                    </span>
                ),
                disableSortBy: true,
            });
        }

        return baseColumns;
    }, [showTrainings, userDetails.trainings]);

    // Transform data for TableClient
    const tableData = useMemo(() => {
        return subjects.map((subject, index) => ({
            ...subject,
            id: index, // Add unique ID for each row
        }));
    }, [subjects]);

    return (
        <Row>
            <Col md="12" xs="12" className="content">
                <Block>
                    <SpaceBetweenDiv alignStart>
                        <FlexDiv column>
                            <Heading4 mb={16}>{email}</Heading4>
                            {userDetails.started !== undefined && (
                                <Fragment>
                                    <TextDefault lighter mb={4}>
                                        Started on{' '}
                                        {formatDate(userDetails.started)}
                                    </TextDefault>
                                    <FlexDiv alignCenter>
                                        <ProgressWithNumber
                                            value={score}
                                            width={330}
                                            height={6}
                                        />
                                    </FlexDiv>
                                </Fragment>
                            )}
                        </FlexDiv>
                        <RequireRole>
                            {!showTrainings && (
                                <TooltipWrapper
                                    showTooltip={!canRemind}
                                    tooltipText={
                                        'You can send reminders only when a training is active'
                                    }
                                >
                                    <ThemedButton
                                        mode="primary"
                                        disabled={!canRemind}
                                        onClick={() => {
                                            onSendReminder();
                                        }}
                                        text={
                                            <>
                                                {loadingCanRemind ? (
                                                    <Spinner
                                                        as="span"
                                                        size="sm"
                                                    />
                                                ) : (
                                                    ' Send a reminder'
                                                )}
                                            </>
                                        }
                                    />
                                </TooltipWrapper>
                            )}
                        </RequireRole>
                    </SpaceBetweenDiv>

                    {/* Show Alert instead of table if company has company.anonymousTracking */}
                    {anonymousTracking ? (
                        <Alert color="warning">{trackingDisabled}</Alert>
                    ) : (
                        <TableClient
                            columns={columns}
                            data={tableData}
                            hidePagination={tableData.length <= 50}
                            initialPageSize={50}
                            defaultSortBy="delivered"
                            defaultSortDescent={false}
                            getRowProps={(row) => ({
                                className: classnames('status-row', {
                                    disabled: row.original.status === 0,
                                }),
                            })}
                        />
                    )}
                </Block>
            </Col>
        </Row>
    );
}

export default observer(UserCampaignDetails);
